import axios from "axios";
import lodash from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { shallow } from "zustand/shallow";

import {
  enums,
  enumsTypes,
  ITEM_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS_MAP,
} from "@shared/constants";

import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import { TrackTokenDataType } from "@shared/lib/analytics/types/analytics-types";
import { useStore } from "@shared/store/useStore";

import { DataProvider } from "@shared/constants/common";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import type {
  ModifyBasket,
  TicketBasket,
  UpdateGuestQuestions,
  VenueCustomFee,
  VenueInternalFee,
} from "../types";
import { TicketItemGroupType } from "../types/common/ticket-item-group/ticket-item-group-type";
import {
  getBoxOfficePurchasePlatform,
  getPublicPurchasePlatform,
} from "../utils/basket-utils";
import {
  checkItemGroupsHavePermissions,
  consolidate,
  convertToItemGroups as defaultConvertToItemGroups,
  expire as expireBasketInRepo,
  filterItemGroups,
  getSelectedCustomSubTicketOptions,
  invalidateQueriesOnBasketError,
  mergeMemberships,
  persist as persistBasket,
  removeEmptyItemGroups,
  ToModifyItem,
  filterOutProductPackageItemGroupsThatWillBeModified,
} from "../utils/helpers";
import { normalizeAddress } from "../utils/address-utils";
import { regularItemGroups } from "../utils/ticket-basket-utils";
import {
  cleanRefundProtectionToken,
  hasEligibleItemsForProtection,
} from "./refundProtection/helpers";
import { BasketRequestQueue } from "./BasketRequestQueue/BasketRequestQueue";

const requestQueue = BasketRequestQueue.getInstance();

/**
 * Wrapper around persist that handles basket request queueing.
 * Ensures requests are processed sequentially and that the latest basket state
 * is used at execution time (not queue time).
 */
async function persist(
  item_groups: Array<TicketItemGroupType>,
  type: DataProvider,
  config: {
    basket?: TicketBasket;
    injects?: Record<string, unknown>;
  }
): Promise<TicketBasket> {
  // Queue the request with a callback that gets the latest basket at execution time
  return requestQueue.addBasketRequest(async () => {
    // Get the latest basket state at execution time (not queue time)

    const latestBasket = useStore.getState().getBasket(type);

    // Prepare the config with the latest basket data
    const updatedConfig = {
      ...config,
      basket: {
        ...config.basket,
        ...(latestBasket &&
          "checkout_idempotency_key" in latestBasket && {
            checkout_idempotency_key:
              latestBasket.checkout_idempotency_key as string,
          }),
      },
    };

    // Call the actual persist function
    const result = await persistBasket(item_groups, type, updatedConfig);

    // Update the store with the response
    useStore.getState().setBasket(type, result);

    return result;
  });
}

/**
 * Wrapper around expire that handles basket request queueing.
 * Ensures requests are processed sequentially.
 */
async function expire(
  type: DataProvider,
  basket: TicketBasket,
  injects?: Record<string, unknown>
): Promise<void> {
  // Queue the request
  return requestQueue.addBasketRequest(async () => {
    await expireBasketInRepo(type, basket, injects);
  });
}

/**
 * Basic basket hook intended for use by extending
 * useVenueBasket or usePublicBasket for any custom configurations
 */
export function useBasket(
  type: DataProvider,
  config: TicketBasket & { venueId?: number } = {
    purchase_source_platform:
      type === DataProvider.Venue
        ? getBoxOfficePurchasePlatform().id
        : getPublicPurchasePlatform(),
    payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
  },
  {
    convertToItemGroups,
  }: {
    // add any custom logic to prepare item groups
    // defaults to convertToItemGroups
    convertToItemGroups: (
      items: Array<{
        item: unknown;
        quantity: number;
        parent?: number | null;
        pay_what_you_can_price?: string | null;
        is_upgraded?: boolean;
        // TODO: should refactor to always pass itemType
        itemType?: enumsTypes.ItemTypesMapIdType;
      }>
    ) => Array<TicketItemGroupType>;
  } = {
    convertToItemGroups: defaultConvertToItemGroups,
  },
  rawBasket?: TicketBasket,
  setRawBasket?: (basket: TicketBasket) => void
) {
  const queryClient = useQueryClient();
  const [isExpired, setIsExpired] = useState(false);

  /**
   * Retrieve our global store getter/setter
   */
  const [storeBasket, setStoreBasket] = useStore(
    (state) => [state.getBasket(type), state.setBasket],
    shallow
  );

  const basket = rawBasket ?? storeBasket;
  const setBasket = setRawBasket ?? setStoreBasket;

  /**
   * Tracks basket updates
   */
  const [isBasketUpdating, setIsBasketUpdating] = useStore(
    (state) => [state.isBasketUpdating, state.setIsBasketUpdating],
    shallow
  );

  const [isPurchasing, setIsPurchasing] = useStore(
    (state) => [state.isPurchasing, state.setIsPurchasing],
    shallow
  );

  /**
   * Axios error
   */
  const [basketError] = useStore(
    (state) => [state.basketError, state.setBasketError],
    shallow
  );

  /**
   * Empties the local state of the basket.
   * Note that this does not expire the basket on the backend.
   */
  const emptyBasket = useCallback(() => {
    // Workaround -- When the hook-scoped `basket` is used here, it contains an `undefined` value,
    // so we must reference the store directly.
    const basket = useStore.getState().getBasket(type);
    if (basket) {
      setBasket(type);
    }
  }, [setBasket, type]);

  const handleBasketError = useCallback(
    (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.data) {
        const { detail } = error.response.data;
        if (detail && typeof detail === "string") {
          if (detail.startsWith("Your basket has expired")) {
            return emptyBasket();
          }
        }
        return invalidateQueriesOnBasketError(type, queryClient, error);
      }
      return undefined;
    },
    [type, queryClient, emptyBasket]
  );

  const analytics = useAnalytics();

  const discountMessageObject = useMemo<
    TicketBasket["discount_message_object"]
  >(() => basket?.discount_message_object, [basket?.discount_message_object]);

  const enforceBoxOfficeInfoCollection = useMemo(
    () => Boolean(basket?.enforce_box_office_info_collection),
    [basket?.enforce_box_office_info_collection]
  );

  /**
   * The supported card types of the basket
   */
  const supportedCCTypes = useMemo(
    () => basket?.supported_cc_types ?? [],
    [basket?.supported_cc_types]
  );

  /**
   * Check if the basket has items
   *
   * @returns {boolean}
   */
  const hasItems = useMemo(
    () => (basket?.item_groups?.length ?? 0) > 0,
    [basket?.item_groups]
  );

  /**
   * Get the items in the basket. Note, in python I think this is saved as a set
   * because the order looks to jump around. To make things remain consistent
   * we sort the items by their id.
   */
  const itemGroups = useMemo(() => {
    return (basket?.item_groups ?? []).sort(
      (a, b) => (a?.id ?? 0) - (b?.id ?? 0)
    );
  }, [basket?.item_groups]);

  /**
   * Get the expiry date of the basket
   *
   * @returns {Date|undefined}
   */
  const expiryDate = useMemo<Date | undefined>(() => {
    const date = basket?.expiry_date ? new Date(basket.expiry_date) : undefined;

    // Check if InvalidDate
    return Number.isFinite(date?.getTime()) ? date : undefined;
  }, [basket?.expiry_date]);

  useEffect(() => {
    setIsExpired(!expiryDate || expiryDate.getTime() <= Date.now());
  }, [expiryDate]);

  const clearIsExpired = useCallback(() => {
    setIsExpired(false);
  }, []);

  const basketId = useMemo(() => basket?.id, [basket?.id]);

  /**
   * Get the name_on_ticket field on the basket, default is an empty string
   */
  const nameOnTicket = useMemo(
    () => basket?.name_on_ticket ?? "",
    [basket?.name_on_ticket]
  );

  /**
   * Get the status of the basket, default is pending status
   */
  const status = useMemo(
    () => basket?.status ?? ITEM_STATUS_OPTIONS.PENDING.id,
    [basket?.status]
  );

  /**
   * Get the is_waitlist status of the basket
   */
  const isWaitlist = useMemo(
    () => basket?.is_waitlist ?? false,
    [basket?.is_waitlist]
  );

  /**
   * Get the discount_amount field value
   *
   * @returns {number}
   */
  const discountAmount = useMemo(
    () => basket?.discount_amount ?? 0,
    [basket?.discount_amount]
  );

  /**
   * Get the payment_plan_total_fees field value
   *
   * @returns {number}
   */
  const paymentPlanTotalFees = useMemo(
    () => basket?.payment_plan_total_fees ?? 0,
    [basket?.payment_plan_total_fees]
  );

  /**
   * Get the service_fees field value
   *
   * @returns {number}
   */
  const serviceFees = useMemo(
    () => basket?.service_fees ?? 0,
    [basket?.service_fees]
  );

  /**
   * Get the total_price field value
   *
   * @returns {number}
   */
  const totalPrice = useMemo(
    () => basket?.total_price ?? "0.00",
    [basket?.total_price]
  );

  /**
   * Get the currency field value
   * Use CAD as the default*
   *
   * *Note that this fallback is inaccurate as usages of this value are likely doing fallbacks of their own;
   * eg. useBasket.currency ?? venue.currency
   *
   * Fallbacks at this level would then always default to CAD
   *
   * TODO clean this up
   *
   * @returns {string}
   */
  const currency = useMemo(
    //
    () => basket?.currency ?? enums.CURRENCY_MAP.CAD.id,
    [basket?.currency]
  );

  /**
   * Get the basket_subtotal1 field value
   *
   * @returns {string}
   */
  const subtotal = useMemo(() => basket?.subtotal_1 ?? 0, [basket?.subtotal_1]);

  /**
   * Check if any item_groups have a payment plan
   *
   * @returns {boolean}
   */
  const hasPaymentPlan = useMemo(
    () => basket?.item_groups && basket.item_groups.some((g) => g.payment_plan),
    [basket?.item_groups]
  );

  /**
   * Returns the venue_internal_fees array
   *
   * @returns {Array<VenueInternalFee>}
   */
  const venueInternalFees = useMemo(
    () => basket?.venue_internal_fees ?? [],
    [basket?.venue_internal_fees]
  );

  /**
   * Returns the venue_custom_fees array
   *
   * @returns {Array<VenueCustomFee>}
   */
  const venueCustomFees = useMemo(
    () => basket?.venue_custom_fees ?? [],
    [basket?.venue_custom_fees]
  );

  const messageToCustomer = useMemo(
    () => basket?.message_to_customer,
    [basket?.message_to_customer]
  );

  const purchaseNote = useMemo(
    () => basket?.purchase_note,
    [basket?.purchase_note]
  );

  const customerGroupId = useMemo(
    () => basket?.customer_group_id,
    [basket?.customer_group_id]
  );

  const customerInfo = useMemo(() => {
    const name = basket?.name_on_ticket?.split(" ");
    return {
      info_email: basket?.info_email || basket?.email || "",
      info_first_name: (basket?.info_first_name || name?.[0]) ?? "",
      info_last_name: (basket?.info_last_name || name?.[1]) ?? "",
      info_phone_number:
        basket?.info_phone_number || basket?.phone_number || "",
    };
  }, [
    basket?.info_email,
    basket?.info_first_name,
    basket?.info_last_name,
    basket?.info_phone_number,
    basket?.email,
    basket?.phone_number,
    basket?.name_on_ticket,
  ]);

  const enhancedInfoQuestions = useMemo(() => {
    return {
      info_birthday: basket?.info_birthday || "",
      info_company: basket?.info_company || "",
      info_job_title: basket?.info_job_title || "",
      info_license_plate: basket?.info_license_plate || "",
      info_student_number: basket?.info_student_number || "",
    };
  }, [
    basket?.info_birthday,
    basket?.info_company,
    basket?.info_job_title,
    basket?.info_license_plate,
    basket?.info_student_number,
  ]);

  const enhancedInfoAddress = useMemo(() => {
    return {
      info_home_address_street_name:
        basket?.info_home_address_street_name || "",
      info_home_address_city: basket?.info_home_address_city || "",
      info_home_address_country: basket?.info_home_address_country || "",
      info_home_address_postal: basket?.info_home_address_postal || "",
      info_home_address_province: basket?.info_home_address_province || "",
      info_home_address_unit_number:
        basket?.info_home_address_unit_number || "",
    };
  }, [
    basket?.info_home_address_street_name,
    basket?.info_home_address_city,
    basket?.info_home_address_unit_number,
    basket?.info_home_address_postal,
    basket?.info_home_address_province,
    basket?.info_home_address_country,
  ]);

  const customInfoQuestions = useMemo(() => {
    return basket?.info_custom_question_data ?? [];
  }, [basket?.info_custom_question_data]);

  const collectEnhancedInfo = useMemo(() => {
    return {
      collect_info_home_address: Boolean(basket?.collect_info_home_address),
      collect_info_company: Boolean(basket?.collect_info_company),
      collect_info_job_title: Boolean(basket?.collect_info_job_title),
      collect_info_student_number: Boolean(basket?.collect_info_student_number),
      collect_info_birthday: Boolean(basket?.collect_info_birthday),
      collect_info_license_plate: Boolean(basket?.collect_info_license_plate),
    };
  }, [
    basket?.collect_info_home_address,
    basket?.collect_info_company,
    basket?.collect_info_job_title,
    basket?.collect_info_student_number,
    basket?.collect_info_birthday,
    basket?.collect_info_license_plate,
  ]);

  const hasAdditionalInfoCollection = useMemo(() => {
    return Boolean(
      Object.values(collectEnhancedInfo).some((collect) => collect) ||
        customInfoQuestions.length ||
        itemGroups.some((group) => group.info_required)
    );
  }, [collectEnhancedInfo, customInfoQuestions, itemGroups]);

  const hasShippingAddressCollection = useMemo(
    () =>
      itemGroups.some(
        (group) => group.shipping_type === enums.SHIPPING_METHODS.DELIVERY.id
      ),
    [itemGroups]
  );

  const includedInStats = useMemo(
    () => basket?.included_in_stats,
    [basket?.included_in_stats]
  );

  const squareTerminalCheckoutId = useMemo(
    () => basket?.square_terminal_checkout_id,
    [basket?.square_terminal_checkout_id]
  );

  const squareLocationId = useMemo(
    () => basket?.square_location_id,
    [basket?.square_location_id]
  );

  const paymentGateway = useMemo(
    () => basket?.payment_gateway,
    [basket?.payment_gateway]
  );

  const paymentVenue = useMemo(
    () => basket?.payment_venue,
    [basket?.payment_venue]
  );

  const soldByVenue = useMemo(
    () => basket?.sold_by_venue,
    [basket?.sold_by_venue]
  );

  const invoiceId = useMemo(() => basket?.invoice_id, [basket?.invoice_id]);

  const isSquareTerminalPurchase = useMemo(() => {
    return basket?.is_square_terminal_purchase;
  }, [basket?.is_square_terminal_purchase]);

  /**
   * Get the purchase source platform of the basket
   */
  const purchaseSourcePlatform = useMemo(() => {
    let purchase_source_platform =
      enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id;

    if (config?.purchase_source_platform) {
      purchase_source_platform = config.purchase_source_platform;
    }

    if (basket?.purchase_source_platform) {
      purchase_source_platform = basket.purchase_source_platform;
    }

    return purchase_source_platform;
  }, [basket?.purchase_source_platform, config.purchase_source_platform]);

  /**
   * Get the payment type of the basket
   */
  const paymentType = useMemo(() => {
    // default payment type is credit
    let payment_type: number = PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id;

    // payment type from initial config
    if (config?.payment_type) {
      payment_type = config?.payment_type;
    }
    // if basket has a payment type, use that
    if (basket?.payment_type) {
      payment_type = basket.payment_type;
    }

    return payment_type;
  }, [basket?.payment_type, config.payment_type]);

  /**
   * Returns the total amount of venue_internal_fees
   * Only include fees where is_matched = true and the fee_type is not FEE_TAX
   *
   * @returns {number}
   */
  const internalFeesTotal = useMemo(() => {
    return (
      basket?.venue_internal_fees?.reduce(
        (total: number, current: VenueInternalFee) => {
          let newTotal = total;
          if (
            current.is_matched &&
            current.fee_type !== enums.VENUE_INTERNAL_FEE_TYPE_MAP.FEE_TAX.id
          ) {
            // Taxes on internal fees are included as a custom fees (getcustomFeesGroupedByName)
            // in order to group it with other custom taxes.
            newTotal = Number(newTotal) + Number(current.fee_total);
          }
          return newTotal;
        },
        0
      ) ?? 0
    );
  }, [basket?.venue_internal_fees]);

  /**
   * Returns the basket's venue_custom_fees grouped by name.
   * Object format: {$customFees, $fees, $taxes}
   *
   * Includes taxes on internal fees and non-absorbed custom fees.
   *
   * @returns {Object}
   */
  const customFeesGroupedByName = useMemo(() => {
    const feeTypes = enums.VENUE_CUSTOM_FEE_TYPE_MAP;
    const internalFeeTypes = enums.VENUE_INTERNAL_FEE_TYPE_MAP;

    let $customFees: Array<VenueCustomFee | VenueInternalFee> = []; // all custom fees and taxes
    let $fees: Array<VenueCustomFee | VenueInternalFee> = []; // only fees
    let $taxes: Array<VenueCustomFee | VenueInternalFee> = []; // only taxes

    if (!basket || !basket.venue_custom_fees) {
      return { $customFees, $fees, $taxes };
    }
    // Only display non-absorbed fees to the customer
    const matchedCustomFees: Array<VenueCustomFee | VenueInternalFee> = lodash
      .cloneDeep(basket.venue_custom_fees)
      .filter((fee: VenueCustomFee) => !fee.is_absorbed);

    const taxOnCustomFees: Array<VenueCustomFee> = [];

    matchedCustomFees.forEach((fee: VenueCustomFee | VenueInternalFee) => {
      if ((fee as VenueCustomFee).taxes_applied_to_fee) {
        (fee as VenueCustomFee).taxes_applied_to_fee.forEach((fee) => {
          taxOnCustomFees.push(fee);
        });
      }
    });

    // Included taxes on internal fees in this separate grouping as we do not want to display taxes on fees as
    // part of the service fees.
    let taxesOnInternalFees: Array<VenueInternalFee> = [];

    if (basket.venue_internal_fees) {
      taxesOnInternalFees = lodash.cloneDeep(
        basket.venue_internal_fees.filter(
          (fee) =>
            fee.fee_type === internalFeeTypes.FEE_TAX.id && fee.is_matched
        )
      );
    }

    const fees: Array<VenueCustomFee | VenueInternalFee> = [];

    const matchedFees = matchedCustomFees.concat(
      taxesOnInternalFees,
      taxOnCustomFees
    );

    matchedFees.forEach((fee) => {
      const feeIsGrouped = fees.find(
        (el) => el.name === fee.name && el.tax_number === fee.tax_number
      );
      if (feeIsGrouped) {
        feeIsGrouped.fee_total =
          Number(feeIsGrouped.fee_total) + Number(fee.fee_total);
      } else {
        fees.push(fee);
      }
    });

    // move taxes to the top, so taxes are always displayed at the top.
    const ordered_custom_fees: Array<VenueCustomFee | VenueInternalFee> = [];
    const ordered_taxes: Array<VenueCustomFee | VenueInternalFee> = [];
    const ordered_fees: Array<VenueCustomFee | VenueInternalFee> = [];

    fees.forEach((fee) => {
      if (
        fee.fee_type === feeTypes.TAX.id ||
        fee.fee_type === internalFeeTypes.FEE_TAX.id
      ) {
        ordered_custom_fees.push(fee);
        ordered_taxes.push(fee);
      }
      if (
        fee.fee_type !== feeTypes.TAX.id &&
        fee.fee_type !== internalFeeTypes.FEE_TAX.id
      ) {
        ordered_custom_fees.push(fee);
        ordered_fees.push(fee);
      }
    });

    $customFees = ordered_custom_fees; // all custom fees and taxes
    $fees = ordered_fees; // only fees
    $taxes = ordered_taxes; // only taxes

    return { $customFees, $fees, $taxes };
  }, [basket]);

  const shippingAddress = useMemo(() => {
    return normalizeAddress(basket?.shipping_address);
  }, [basket]);

  const billingAddress = useMemo(() => {
    return normalizeAddress(basket?.billing_address);
  }, [basket]);

  const shippingCost = useMemo(() => {
    return basket?.shipping_cost;
  }, [basket]);

  /**
   * Returns the child tickets in a ticket package.
   * If the $event field exists on the child tickets, they will be sorted
   * by start time.
   *
   * @returns {Array<TicketItemGroupType>}
   */
  const getSubItemGroups = useCallback(
    (group?: TicketItemGroupType) => {
      const subgroups: Array<
        TicketItemGroupType & { $event?: { starts_on: string } }
      > = [];
      if (group && !group.parent) {
        basket?.item_groups?.forEach((ig) => {
          if (ig.parent === group.id) {
            subgroups.push(ig);
          }
        });
      }
      // sort into chronological order
      if (subgroups[0] && subgroups[0].$event) {
        subgroups.sort((a, b) => {
          const eventStartsOnA = a.$event?.starts_on ?? "";
          const eventStartsOnB = b.$event?.starts_on ?? "";

          return eventStartsOnA.localeCompare(eventStartsOnB);
        });
      }

      return subgroups;
    },
    [basket?.item_groups]
  );

  /**
   * Returns total number of items in the basket
   */
  const totalItemQuantity = useMemo(() => {
    if (!basket) {
      return 0;
    }

    return regularItemGroups(basket).reduce((carry, item) => {
      if (item?.quantity) {
        return carry + item.quantity;
      }
      return carry;
    }, 0);
  }, [basket]);

  /**
   * Add an item to the basket. Takes in a single or multiple items and converts
   * them into item groups before consolidating all item groups and persisting
   * them to the backend.
   */
  const addItems = useCallback(
    async (
      toAdd:
        | {
            item: unknown;
            quantity: number;
            parent?: number | null;
            pay_what_you_can_price?: string | null;
          }
        | Array<{
            item: unknown;
            quantity: number;
            parent?: number | null;
            pay_what_you_can_price?: string | null;
          }>,
      basketConfig?: TicketBasket
    ): Promise<TicketBasket | undefined> => {
      if (isBasketUpdating) {
        return undefined;
      }

      try {
        setIsBasketUpdating(true);

        // eslint-disable-next-line no-param-reassign
        toAdd = Array.isArray(toAdd) ? toAdd : [toAdd];

        // create a new array to store the basket's item_groups
        // this prevents changes to the item_groups in the case of an error
        const newItemGroups = lodash.cloneDeep(
          basketConfig?.item_groups ?? basket?.item_groups ?? []
        );

        const consolidatedItems = consolidate(
          mergeMemberships([
            ...mergeMemberships(newItemGroups),
            ...convertToItemGroups(toAdd),
          ])
        );

        // if we are adding items, we need to clean the refund protection token
        // otherwise, we can just use the basket as is -- for extending expiry, etc.
        const basketToPersist =
          toAdd.length > 0
            ? cleanRefundProtectionToken({
                ...config,
                ...basket,
                ...basketConfig,
              })
            : {
                ...config,
                ...basket,
                ...basketConfig,
              };

        const newBasket = await persist(consolidatedItems, type, {
          basket: basketToPersist,
          injects: { venueId: config?.venueId },
        });

        setBasket(type, newBasket);

        return newBasket;
      } catch (error) {
        handleBasketError(error);
        throw error;
      } finally {
        setIsBasketUpdating(false);
      }
    },
    [basket, config, isBasketUpdating, setBasket, setIsBasketUpdating, type]
  );

  const modifyItems = useCallback(
    async (
      toModify:
        | {
            item: unknown;
            quantity: number;
            pay_what_you_can_price?: string | null;
          }
        | Array<{
            item: unknown;
            quantity: number;
            pay_what_you_can_price?: string | null;
          }>,
      basketConfig?: TicketBasket
    ): Promise<TicketBasket | undefined> => {
      logAppInfo(`isBasketUpdating: ${isBasketUpdating}`);
      if (isBasketUpdating) {
        return undefined;
      }

      const clonedBasketConfig = lodash.cloneDeep(basketConfig);

      try {
        setIsBasketUpdating(true);

        const toModifyItems = Array.isArray(toModify) ? toModify : [toModify];

        /*
         * Since product packages are always split quantity 1 per item group, every time we modify
         * we need to first get rid of existing item groups so that we dont double count
         */
        const newItemGroups =
          filterOutProductPackageItemGroupsThatWillBeModified(
            toModifyItems as ToModifyItem[],
            // create a new array to store the basket's item_groups
            // this prevents changes to the item_groups in the case of an error
            lodash.cloneDeep(
              clonedBasketConfig?.item_groups ?? basket?.item_groups ?? []
            )
          );

        const consolidatedItems = removeEmptyItemGroups(
          consolidate(
            mergeMemberships(
              [
                // consolidate items in basket before consolidating with new items
                // prevents items in basket from being reset to quantity 1 wehen there are more than 1
                ...consolidate(mergeMemberships(newItemGroups)),
                ...convertToItemGroups(toModifyItems),
              ],
              false
            ),
            false
          )
        );

        // modify custom package selections before sending basket to the backend
        if (clonedBasketConfig?.selected_custom_sub_ticket_options) {
          const basketSelections = lodash.cloneDeep(
            basket?.selected_custom_sub_ticket_options ?? []
          );
          clonedBasketConfig.selected_custom_sub_ticket_options =
            getSelectedCustomSubTicketOptions(
              clonedBasketConfig?.selected_custom_sub_ticket_options,
              basketSelections
            );
        }

        const newBasket = await persist(consolidatedItems, type, {
          basket: cleanRefundProtectionToken({
            ...config,
            ...basket,
            ...clonedBasketConfig,
          }),
          injects: { venueId: config?.venueId },
        });

        setBasket(type, newBasket);

        return newBasket;
      } catch (error) {
        handleBasketError(error);
        throw error;
      } finally {
        setIsBasketUpdating(false);
      }
    },
    [config, basket, isBasketUpdating, setBasket, setIsBasketUpdating, type]
  );

  /**
   * Updates an item group in the basket given an item group id.
   * Optionally persists to the basket given the persist flag.
   */
  const modifyItemGroup = useCallback(
    async (
      toModify: {
        itemGroupId: unknown;
        fieldsToUpdate: Partial<TicketItemGroupType>;
      },
      {
        basketConfig,
        shouldPersist = false,
      }: { basketConfig?: TicketBasket; shouldPersist?: boolean } = {}
    ) => {
      const { itemGroupId, fieldsToUpdate } = toModify;
      const newItemGroups = removeEmptyItemGroups(
        itemGroups.map((itemGroup) => {
          if (itemGroup.id === itemGroupId) {
            return {
              ...itemGroup,
              ...fieldsToUpdate,
            };
          }

          return itemGroup;
        })
      );

      let newBasket;

      if (shouldPersist) {
        try {
          setIsBasketUpdating(true);

          newBasket = await persist(newItemGroups, type, {
            basket: cleanRefundProtectionToken({
              ...config,
              ...basket,
              ...basketConfig,
            }),
            injects: { venueId: config?.venueId },
          });
          setBasket(type, newBasket);
          return newBasket;
        } catch (error) {
          handleBasketError(error);
          throw error;
        } finally {
          setIsBasketUpdating(false);
        }
      } else {
        newBasket = { ...basket, item_groups: newItemGroups };
        setBasket(type, newBasket);
        return newBasket;
      }
    },
    [
      basket,
      itemGroups,
      type,
      config,
      setBasket,
      isBasketUpdating,
      setIsBasketUpdating,
    ]
  );

  /** Update item groups in the basket */
  const modifyItemGroups = useCallback(
    async (
      toModify: Array<{
        itemGroupId: unknown;
        fieldsToUpdate: Partial<TicketItemGroupType>;
      }>,
      {
        basketConfig,
        shouldPersist = false,
        preserveRefundProtection = false,
      }: {
        basketConfig?: TicketBasket;
        shouldPersist?: boolean;
        preserveRefundProtection?: boolean;
      } = {}
    ) => {
      const clonedBasketConfig = lodash.cloneDeep(basketConfig);

      // create a new array to store the basket's item_groups
      // this prevents changes to the item_groups in the case of an error
      const clonedItemGroups = lodash.cloneDeep(
        clonedBasketConfig?.item_groups ?? basket?.item_groups ?? []
      );

      const newItemGroups: TicketItemGroupType[] = clonedItemGroups.map(
        (itemGroup) => {
          const group = toModify.find(
            (item) => item.itemGroupId === itemGroup.id
          );

          if (group) {
            return {
              ...itemGroup,
              ...group?.fieldsToUpdate,
            };
          }

          return itemGroup;
        }
      );

      let newBasket;

      if (shouldPersist) {
        try {
          setIsBasketUpdating(true);

          const mergedBasket = {
            ...config,
            ...basket,
            ...clonedBasketConfig,
          };

          newBasket = await persist(newItemGroups, type, {
            basket: preserveRefundProtection
              ? mergedBasket
              : cleanRefundProtectionToken(mergedBasket),
            injects: { venueId: config?.venueId },
          });
          setBasket(type, newBasket);
          return newBasket;
        } catch (error) {
          handleBasketError(error);
          throw error;
        } finally {
          setIsBasketUpdating(false);
        }
      } else {
        newBasket = {
          ...config,
          ...basket,
          ...clonedBasketConfig,
          item_groups: newItemGroups,
        };
        setBasket(type, newBasket);
        return newBasket;
      }
    },
    [
      basket,
      itemGroups,
      type,
      config,
      setBasket,
      isBasketUpdating,
      setIsBasketUpdating,
    ]
  );

  /**
   * Remove an item from the basket. Takes in a single or multiple items and
   * removes them from the basket before persisting the new basket to the
   * backend. If no items remain it expires the basket.
   */
  const removeItems = useCallback(
    async (
      toRemove: TicketItemGroupType | Array<TicketItemGroupType>,
      basketConfig?: TicketBasket,
      seatPermissionsToRemove?: Array<number>
    ) => {
      if (isBasketUpdating) {
        return undefined;
      }

      try {
        setIsBasketUpdating(true);

        const removingItems = Array.isArray(toRemove) ? toRemove : [toRemove];
        const itemGroupsToUse = basketConfig?.item_groups ?? itemGroups ?? [];

        const remaining = filterItemGroups(
          itemGroupsToUse,
          removingItems,
          seatPermissionsToRemove
        );

        itemGroupsToUse.filter((item) => {
          return !removingItems.some((toRemoveItem) => {
            return lodash.isEqual(item, toRemoveItem);
          });
        });

        const newBasket = await persist(remaining, type, {
          basket: cleanRefundProtectionToken({
            ...config,
            ...basket,
            ...basketConfig,
          }),
          injects: { venueId: config?.venueId },
        });

        setBasket(type, newBasket);

        return newBasket;
      } catch (error) {
        handleBasketError(error);
        throw error;
      } finally {
        setIsBasketUpdating(false);
      }
    },
    [
      isBasketUpdating,
      setIsBasketUpdating,
      itemGroups,
      type,
      basket,
      config,
      setBasket,
    ]
  );

  /**
   * Expires the basket if it exists
   */
  const expireBasket = useCallback(async () => {
    if (isBasketUpdating) {
      return;
    }

    try {
      setIsBasketUpdating(true);

      if (basket?.id) {
        setBasket(type);
        await expire(type, basket, { venueId: config?.venueId });
      }
    } catch (error) {
      handleBasketError(error);
    } finally {
      setIsBasketUpdating(false);
    }
  }, [isBasketUpdating, setIsBasketUpdating, basket, type, config, setBasket]);

  const authorizeBasket = useCallback(
    (password_session_id: string) => {
      setBasket(type, {
        ...basket,
        password_session_id,
      });
    },
    [basket, setBasket, type]
  );

  /**
   * Extends the basket expiry
   */
  const extendBasket = useCallback(() => {
    addItems([]);
  }, [addItems]);

  const modifyBasket: ModifyBasket = async (
    fieldsToUpdate,
    { basketConfig, shouldPersist = false } = {}
  ) => {
    let newBasket: TicketBasket = {};

    const basket = useStore.getState().getBasket(type);

    if (shouldPersist) {
      try {
        setIsBasketUpdating(true);

        newBasket = await persist(itemGroups, type, {
          basket: {
            ...cleanRefundProtectionToken({
              ...config,
              ...basket,
              ...basketConfig,
            }),
            // if the refund protection token is in fieldsToUpdate, it will be re-added here after being removed.
            ...fieldsToUpdate,
          },
          injects: { venueId: config?.venueId },
        });
        setBasket(type, newBasket);
        return newBasket;
      } catch (error) {
        handleBasketError(error);
        throw error;
      } finally {
        setIsBasketUpdating(false);
      }
    } else {
      newBasket = { ...config, ...basket, ...basketConfig, ...fieldsToUpdate };
      setBasket(type, newBasket);
      return newBasket;
    }
  };

  const upgradeItemGroup = useCallback(
    async (
      originalItemGroup: TicketItemGroupType,
      upgradeConfig: {
        // Ticket type, membership level, etc.
        itemData: unknown;
        itemType: enumsTypes.ItemTypesMapIdType;
        quantity: number;
      },
      {
        shouldPersist = false,
        basketConfig,
      }: {
        shouldPersist?: boolean;
        basketConfig?: TicketBasket;
      } = {}
    ) => {
      const { itemData, itemType, quantity } = upgradeConfig;

      let newBasket: TicketBasket = {};
      const basket = useStore.getState().getBasket(type);

      const upgradeItemGroup = convertToItemGroups([
        {
          item: itemData,
          quantity,
          is_upgraded: true,
          itemType,
        },
      ]);

      // remove original item group
      const newItemGroups = itemGroups.filter(
        (group) => group.id !== originalItemGroup.id
      );

      // add upgrade item group
      newItemGroups.push(...upgradeItemGroup);

      if (shouldPersist) {
        try {
          setIsBasketUpdating(true);

          newBasket = await persist(newItemGroups, type, {
            basket: {
              ...config,
              ...basket,
              ...basketConfig,
            },
            injects: { venueId: config?.venueId },
          });
          setBasket(type, newBasket);
          return newBasket;
        } catch (error) {
          handleBasketError(error);
          throw error;
        } finally {
          setIsBasketUpdating(false);
        }
      } else {
        newBasket = {
          ...config,
          ...basket,
          ...basketConfig,
          item_groups: newItemGroups,
        };
        setBasket(type, newBasket);
        return newBasket;
      }
    },
    [config, basket, setBasket, type, setIsBasketUpdating, itemGroups]
  );

  const enableExtendedExpiry = (
    enable = true,
    {
      shouldPersist,
    }: {
      shouldPersist: boolean;
    } = {
      shouldPersist: true,
    }
  ) => {
    return modifyBasket(
      {
        extended_expiry_enabled: enable,
        refund_protection_token: basket?.refund_protection_token ?? null,
      },
      {
        shouldPersist,
      }
    );
  };

  const setStatus = useCallback(
    (
      status: (typeof ITEM_STATUS_OPTIONS)[keyof typeof ITEM_STATUS_OPTIONS]["id"]
    ) => {
      return modifyBasket(
        {
          status,
        },
        {
          // False by default. Under most circumstances, there should be no reason for the frontend to be directly dictating the status of a basket.
          // If it does, it should just be to maintain consistency with the backend's basket state.
          shouldPersist: false,
        }
      );
    },
    [modifyBasket]
  );

  const updateCustomer = (
    {
      id,
      name,
      first_name,
      last_name,
      email,
      phone,
    }: {
      id: number | undefined;
      name: string;
      first_name?: string;
      last_name?: string;
      email: string;
      phone?: string;
    },
    { shouldPersist = false } = {}
  ) => {
    return modifyBasket(
      {
        user_id: id,
        name_on_ticket: name,
        first_name,
        last_name,
        email,
        phone_number: phone,
      },
      { shouldPersist }
    );
  };

  const updatePurchaserInfo = (purchaser: {
    name_on_ticket: string;
    email: string;
    phone_number: string;
    subscribe_to_company?: boolean;
    subscribe_to_venue?: boolean;
  }) => {
    return modifyBasket(purchaser, { shouldPersist: false });
  };

  const updateBillingAddress = (
    billing_address: TicketBasket["billing_address"],
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket({ billing_address }, { shouldPersist });
  };

  const updateShippingAddress = (
    shipping_address: TicketBasket["shipping_address"],
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket({ shipping_address }, { shouldPersist });
  };

  const updatePaymentType = (
    newPaymentType: number,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket(
      { payment_type: newPaymentType },
      {
        // Updating payment_type potentially changes fees, so pay attention and make sure
        // you are syncing with the backend accordingly.
        shouldPersist:
          shouldPersist == null
            ? paymentType !== newPaymentType
            : shouldPersist,
      }
    );
  };

  const paymentMethod = basket?.payment_method ?? null;

  /**
   * Updates the payment_method field on the basket.
   */
  const updatePaymentMethod = (
    newPaymentMethod: number | null,
    { shouldPersist = true }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket(
      {
        payment_method: newPaymentMethod,
      },
      {
        shouldPersist,
      }
    );
  };

  const yunoPaymentMethod = basket?.yuno_payment_method ?? null;

  /**
   * Updates the yuno_payment_method field on the basket (Yuno vaulted token).
   */
  const updateYunoPaymentMethod = (
    newYunoPaymentMethod: string | null,
    { shouldPersist = false }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket(
      {
        yuno_payment_method: newYunoPaymentMethod,
      },
      {
        shouldPersist,
      }
    );
  };

  const saveUserPaymentMethod = basket?.save_user_payment_method ?? false;

  const updateSaveUserPaymentMethod = (
    newSaveUserPaymentMethod: boolean,
    { shouldPersist = false }: { shouldPersist?: boolean } = {}
  ) => {
    return modifyBasket(
      {
        save_user_payment_method: newSaveUserPaymentMethod,
      },
      {
        shouldPersist,
      }
    );
  };

  const updateIsStripeWalletPayment = (isStripeWalletPayment: boolean) => {
    return modifyBasket(
      {
        is_stripe_wallet_payment: isStripeWalletPayment,
      },
      {
        shouldPersist: false,
      }
    );
  };

  const save = async (opts?: { withProtectionToken?: boolean }) => {
    const { withProtectionToken = true } = opts ?? {};
    return modifyBasket(
      // maintain the refund protection token if it exists
      // only changes that include updates to item groups should remove the refund protection token
      {
        refund_protection_token:
          withProtectionToken && basket?.refund_protection_token
            ? basket?.refund_protection_token
            : null,
      },
      {
        shouldPersist: true,
      }
    );
  };

  /**
   * Checks to see if any items in the basket require seat selection
   * for a custom package.
   *
   * If event param is passed in, it will only return true if passed event
   * requires selection.
   *
   * @param {Integer} eventId
   * @returns {Boolean}
   */
  const itemsRequiresSeatSelection = (eventId = null): boolean => {
    // search through item groups to find any items that still require seat selection
    const requiresSelection =
      itemGroups &&
      itemGroups.find(
        (item: TicketItemGroupType) => item.requires_seat_or_location_selection
      );

    if (requiresSelection) {
      // if an item requires seat selection, find the parent of that item so we can
      // get the package slug
      const requiresSelectionParent = itemGroups.find(
        (item: TicketItemGroupType) => item.id === requiresSelection.parent
      );

      // if an eventId is passed, check to see if the parent is the same event as
      // the passed event
      if (eventId) {
        return Boolean(
          requiresSelectionParent?.event === eventId && requiresSelectionParent
        );
      }

      // return the event package that requires selection
      return Boolean(requiresSelectionParent);
    }

    // if requiresSelection is false, return false
    return false;
  };

  const appliedDiscounts = useMemo(() => {
    return {
      discount: basket?.discount,
      discounts_applied: basket?.discounts_applied,
      discount_rule: basket?.discount_rule,
    };
  }, [basket?.discount, basket?.discounts_applied, basket?.discount_rule]);

  const hasDiscountsApplied = useMemo(() => {
    return Boolean(
      basket?.discount ||
        basket?.discounts_applied?.length ||
        basket?.discount_rule
    );
  }, [basket?.discount, basket?.discounts_applied, basket?.discount_rule]);

  const hasEntireOrderDiscount = useMemo(() => {
    if (basket?.discounts_applied && basket?.discounts_applied?.length) {
      return basket.discounts_applied.some(
        (discount) =>
          discount.apply_method === enums.APPLY_TYPE_MAP.APPLY_ONCE.id
      );
    }

    return (
      basket?.discount?.apply_method === enums.APPLY_TYPE_MAP.APPLY_ONCE.id
    );
  }, [basket?.discount, basket?.discounts_applied]);

  const basketProductIds = useMemo(() => {
    return itemGroups
      ?.filter(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id
      )
      ?.map((itemGroup) => itemGroup.product);
  }, [itemGroups]);

  const basketMembershipIds = useMemo(() => {
    return lodash(itemGroups)
      .filter(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      )
      .map((itemGroup) => itemGroup.membership_group)
      .filter()
      .uniq()
      .value();
  }, [itemGroups]);

  const basketMembershipLevelIds = useMemo(() => {
    return lodash(itemGroups)
      .filter(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      )
      .map((itemGroup) => itemGroup.membership_level)
      .filter()
      .uniq()
      .value();
  }, [itemGroups]);

  const applyDiscount = useCallback(
    (code: string) => {
      return modifyBasket(
        { discount_code: code, removed_discount: false },
        { shouldPersist: true }
      );
    },
    [modifyBasket]
  );

  const applyBulkDiscounts = useCallback(
    (codes: string[]) => {
      return modifyBasket(
        { discount_codes: codes, removed_discount: false },
        { shouldPersist: true }
      );
    },
    [modifyBasket]
  );

  const removeDiscounts = useCallback(() => {
    return modifyBasket(
      { discount_code: null, discount: null, removed_discount: true },
      { shouldPersist: true }
    );
  }, [modifyBasket]);

  const creditApplied = useMemo(() => basket?.credit_applied, [basket]);

  const applyCredits = useCallback(
    (type: enumsTypes.ShowpassCreditTypesMapIdType, amount: number) => {
      if (!type || !amount) {
        return null;
      }
      return modifyBasket(
        {
          credit_type: type,
          credit_applied: amount,
        },
        { shouldPersist: true }
      );
    },
    [modifyBasket]
  );

  const removeCredits = useCallback(() => {
    return modifyBasket(
      {
        credit_type: null,
        credit_applied: null,
      },
      { shouldPersist: true }
    );
  }, [modifyBasket]);

  const otherPaymentType = useMemo(
    () => basket?.other_payment_type,
    [basket?.other_payment_type]
  );

  const hasCreditsApplied = useMemo(
    () => Number(creditApplied) > 0,
    [creditApplied]
  );

  const creditType = useMemo(() => basket?.credit_type, [basket?.credit_type]);
  const getItemGroups = useCallback(
    (includeChildren = true) => {
      let groups = lodash(itemGroups);
      if (!includeChildren) {
        groups = groups.reject("parent");
      }
      return groups.value();
    },
    [itemGroups]
  );

  const basketItemTypes = useMemo(
    (includeChildren = true) => {
      const groups = getItemGroups(includeChildren);
      return lodash(groups).map("type").compact().uniq().value();
    },
    [itemGroups]
  );

  const basketEventIds = useMemo(
    (includeChildren = true) => {
      const groups = getItemGroups(includeChildren);
      return lodash(groups).map("event").filter().uniq().value();
    },
    [itemGroups]
  );

  const isGroupSale = useMemo(
    () => Boolean(basket?.is_group_sale),
    [basket?.is_group_sale]
  );

  const areAllRenewalItemGroups = useMemo(() => {
    // the basket is considered a renewal basket only if all item groups in the basket have renewal_member set to non-null (id).
    return (
      itemGroups.length > 0 &&
      itemGroups.every((itemGroup) => itemGroup.renewal_member)
    );
  }, [itemGroups]);

  const eachItemGroupHasSeatOrLocationPermission = useMemo(
    () => checkItemGroupsHavePermissions(itemGroups),
    [itemGroups]
  );

  const itemGroupsWithNoSeatOrLocationPermission = useMemo(() => {
    return itemGroups.filter((item) => {
      // An item group should have EITHER seat OR location permissions equal to quantity
      const hasSufficientSeats =
        item.tt_seat_permissions?.length === item.quantity;
      const hasSufficientLocations =
        item.tt_location_permissions?.length === item.quantity;

      // If it has neither sufficient seats nor sufficient locations, filter it in
      return !(hasSufficientSeats || hasSufficientLocations);
    }).length;
  }, [itemGroups]);

  const isFree = useMemo(() => totalPrice === "0.00", [totalPrice]);

  const selectedCustomSubTicketOptions = useMemo(
    () => basket?.selected_custom_sub_ticket_options ?? [],
    [basket?.selected_custom_sub_ticket_options]
  );

  const waitlist = useMemo(() => {
    return basket?.waitlist;
  }, [basket?.waitlist]);

  /**
   * Triggers all checkout tracking tokens
   *
   * @return {*} promise with list of checkout tracking tokens for the basket
   */
  const triggerCheckoutTrackingTokens = useCallback(() => {
    let tokenObj: TrackTokenDataType = {
      fbEvent: "InitiateCheckout",
      currency,
    };
    if (totalPrice) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tokenObj = { ...tokenObj, value: parseInt(totalPrice, 10) };
    }

    // Don't load tracking tokens if no ticket groups exist
    if (basketEventIds.length !== 0) {
      return analytics.trackToken(
        enums.TRACKING_TOKEN_LOCATIONS.LOCATION_CHECKOUT.id,
        paymentVenue,
        basketEventIds,
        tokenObj
      );
    }
    // if no events are in the basket, return an empty list
    return [];
  }, [currency, totalPrice, basketEventIds, paymentVenue]);

  const email = useMemo(() => basket?.email, [basket?.email]);

  const paymentIntentOutcomeMessage = useMemo(
    () => basket?.payment_intent_outcome_message,
    [basket?.payment_intent_outcome_message]
  );

  const paymentIntentStatus = useMemo(
    () => basket?.payment_intent_status,
    [basket?.payment_intent_status]
  );

  const userId = useMemo(() => basket?.user_id, [basket?.user_id]);

  const phoneNumber = useMemo(
    () => basket?.phone_number ?? null,
    [basket?.phone_number]
  );

  const subscribeToVenue = useMemo(
    () => basket?.subscribe_to_venue ?? false,
    [basket?.subscribe_to_venue]
  );

  /**
   * Update the `accepted_terms` value of ALL item_groups in the basket.
   *
   * Basket terms of use can be considered an all-or-nothing event. If terms are required and one of the item groups do not have accepted terms,
   * the basket cannot be purchased. Hence, this function only accepts one boolean as a parameter.
   */
  const updateAcceptedTerms = async (accept = true) => {
    return modifyItemGroups(
      itemGroups.map((ig) => ({
        itemGroupId: ig.id,
        fieldsToUpdate: {
          accepted_terms: accept,
        },
      })),
      {
        shouldPersist: true,
        preserveRefundProtection: true,
      }
    );
  };

  const updateGuestQuestions = useCallback(
    async ({
      itemGroupId,
      newCustomQuestions,
      newEnhancedInfo,
      guestQuestionsMap,
      modifyBasketFields,
    }: UpdateGuestQuestions) => {
      // for multi-item group baskets with info_required,
      // all item groups must be updated at once
      // otherwise guest question data is lost when filling out different inputs
      if (itemGroupId) {
        const newMap = {
          // clone the original map
          ...guestQuestionsMap.current,
          [itemGroupId]: {
            // for given item group, first clone the original data
            ...guestQuestionsMap.current[itemGroupId],
            // if updated custom questions are provided, use them, or default to original data
            customQuestions:
              newCustomQuestions ??
              guestQuestionsMap.current[itemGroupId]?.customQuestions,
            // if updated enhanced info is provided, use them (spread object), or default to original data
            enhancedInfo: {
              ...guestQuestionsMap.current[itemGroupId]?.enhancedInfo,
              ...(newEnhancedInfo ?? {}),
            },
          },
        };
        // eslint-disable-next-line no-param-reassign
        guestQuestionsMap.current = { ...newMap };

        const refreshedBasket = useStore.getState().getBasket(type);

        const itemGroupsToModify = Object.keys(newMap).map((key) => {
          const itemGroupData = refreshedBasket?.item_groups?.find(
            (ig) => ig.id === Number(key)
          );
          return {
            itemGroupId: Number(key),
            fieldsToUpdate: {
              info_custom_question_data:
                newMap[Number(key)].customQuestions ??
                itemGroupData?.info_custom_question_data ??
                [],
              ...newMap[Number(key)].enhancedInfo,
            },
          };
        });

        await modifyItemGroups(itemGroupsToModify, {
          shouldPersist: false,
          basketConfig: {
            ...refreshedBasket,
          },
        });
      }
      // else modify the entire basket at once
      else if (!itemGroupId && modifyBasketFields) {
        await modifyBasket(modifyBasketFields, {
          shouldPersist: false,
        });
      }
    },
    [modifyItemGroups, modifyBasket]
  );

  const updateExpiryDate = useCallback(
    (expiryDate: string) => {
      return modifyBasket(
        { expiry_date: expiryDate },
        { shouldPersist: false }
      );
    },
    [modifyBasket]
  );

  const holdLinkSlug = useMemo(
    () => basket?.hold_link_slug,
    [basket?.hold_link_slug]
  );

  const holdSlug = useMemo(() => basket?.hold_slug, [basket?.hold_slug]);

  const allRequiredProductPackageSelectionsMade = useMemo(() => {
    return !itemGroups.some((itemGroup) => itemGroup.preset_attribute_applied);
  }, [itemGroups]);

  const hasTickets = useMemo(
    () =>
      itemGroups.some(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      ),
    [itemGroups]
  );
  const hasProducts = useMemo(
    () =>
      itemGroups.some(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id
      ),
    [itemGroups]
  );
  const hasMemberships = useMemo(
    () =>
      itemGroups.some(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
      ),
    [itemGroups]
  );

  const hasGiftCards = useMemo(
    () =>
      itemGroups.some(
        (itemGroup) =>
          itemGroup.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_GIFT_CARD.id
      ),
    [itemGroups]
  );

  /**
   * Determines if the basket itself supports refund protection.
   *
   * Focused on the basket characteristics.
   * Does not account for other factors like feature or venue flags.
   */
  const supportsRefundProtection = useMemo(() => {
    return (
      !isFree &&
      !hasCreditsApplied &&
      !isGroupSale &&
      hasEligibleItemsForProtection(itemGroups)
    );
  }, [itemGroups, hasCreditsApplied, isFree]);

  const refundProtectionToken = useMemo(
    () => basket?.refund_protection_token,
    [basket?.refund_protection_token]
  );

  return {
    allRequiredProductPackageSelectionsMade,
    updateAcceptedTerms,
    userId,
    paymentIntentOutcomeMessage,
    paymentIntentStatus,
    email,
    otherPaymentType,
    modifyBasket,
    nameOnTicket,
    status,
    addItems,
    enforceBoxOfficeInfoCollection,
    paymentGateway,
    paymentVenue,
    soldByVenue,
    currency,
    hasItems,
    subtotal,
    isExpired,
    expiryDate,
    isWaitlist,
    itemGroups,
    discountMessageObject,
    totalPrice,
    basketError,
    modifyItems,
    modifyItemGroup,
    modifyItemGroups,
    paymentType,
    serviceFees,
    expireBasket,
    removeItems,
    upgradeItemGroup,
    extendBasket,
    discountAmount,
    hasPaymentPlan,
    authorizeBasket,
    venueCustomFees,
    supportedCCTypes,
    isBasketUpdating,
    isPurchasing,
    setIsPurchasing,
    getSubItemGroups,
    internalFeesTotal,
    totalItemQuantity,
    venueInternalFees,
    basketId,
    basketUuid: basket?.uuid,
    basketUuid2: basket?.uuid2,
    paymentPlanTotalFees,
    purchaseSourcePlatform,
    customFeesGroupedByName,
    /** @deprecated use basketError directly */
    error: basketError,
    /** @deprecated use isBasketUpdating directly */
    isUpdating: isBasketUpdating,
    messageToCustomer,
    purchaseNote,
    includedInStats,
    squareTerminalCheckoutId,
    squareLocationId,
    invoiceId,
    isSquareTerminalPurchase,
    appliedDiscounts,
    hasEntireOrderDiscount,
    updateCustomer,
    updatePurchaserInfo,
    updatePaymentType,
    updatePaymentMethod,
    updateYunoPaymentMethod,
    updateSaveUserPaymentMethod,
    itemsRequiresSeatSelection,
    save,
    applyDiscount,
    applyBulkDiscounts,
    removeDiscounts,
    creditApplied,
    hasCreditsApplied,
    applyCredits,
    removeCredits,
    creditType,
    basketItemTypes,
    basketEventIds,
    isGroupSale,
    areAllRenewalItemGroups,
    isFree,
    enableExtendedExpiry,
    setStatus,
    emptyBasket,
    selectedCustomSubTicketOptions,
    triggerCheckoutTrackingTokens,
    updateBillingAddress,
    updateShippingAddress,
    updateIsStripeWalletPayment,
    updateGuestQuestions,
    clearIsExpired,
    customerInfo,
    enhancedInfoQuestions,
    enhancedInfoAddress,
    customInfoQuestions,
    collectEnhancedInfo,
    hasAdditionalInfoCollection,
    hasShippingAddressCollection,
    shippingAddress,
    billingAddress,
    shippingCost,
    updateExpiryDate,
    hasDiscountsApplied,
    waitlist,
    basketProductIds,
    basketMembershipIds,
    basketMembershipLevelIds,
    phoneNumber,
    subscribeToVenue,
    eachItemGroupHasSeatOrLocationPermission,
    itemGroupsWithNoSeatOrLocationPermission,
    holdLinkSlug,
    holdSlug,
    paymentMethod,
    yunoPaymentMethod,
    saveUserPaymentMethod,
    hasTickets,
    hasProducts,
    hasMemberships,
    hasGiftCards,
    supportsRefundProtection,
    refundProtectionToken,
    customerGroupId,
    language: basket?.language ?? null,
  };
}

export type UseBasketReturnType = ReturnType<typeof useBasket>;
